import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import { useToast } from '../components/ToastContext';
import { DeskFrame, PhoneFrame } from '../components/AppFrame';
import { menuFor } from '../lib/menu';
import RepHome from './home/RepHome';
import { DESKTOP_QUERY } from '../components/PageBar';
import useMediaQuery from '../hooks/useMediaQuery';
/*
  **My Counts is one screen for everybody and one shape at every width**, which
  is rule 23 and this job's whole point. This file used to hold a completely
  separate held version of it, about 120 lines with its own card, its own words
  and its own buttons, beside the desk one it imported.
*/
import MyCountsView from './cycle/MyCountsView';
import Tasks from './cycle/Tasks';
/*
  **History was two screens that disagreed about how much history there is.**
  The desk one asked for the most recent 50 and the held one for the most recent
  30, so the same rep saw a different number of their own counts depending on
  what they were holding, with nothing on either saying the list was cut short.
*/
import History from './cycle/History';
/*
  **Both of a rep's exports could be quietly short.** Each asked the database
  once and wrote whatever came back, and a request brings back about a thousand
  rows and says nothing about the rest. Rebuilt to Group C's drawing and reading
  a page at a time now.
*/
import MyExports from './cycle/MyExports';
import { valueMapOf } from './cycle/MyCounts';
import { myWarehouseIds } from '../lib/assignments';
import { fetchAll } from '../lib/save';
import { pickCarryOn } from './home/carryOn';
import CountViewOnlyModal from '../components/CountViewOnlyModal';
import AddLocationModal from '../components/AddLocationModal';
import EditRequestModal from '../components/EditRequestModal';
import PageLoading from '../components/PageLoading';
import SaveFailedBar from '../components/SaveFailedBar';
import SystemShell from '../components/SystemShell';
import Appearance from './settings/Appearance';

/*
  **This file re-exported four components for ManagerDashboard to import from
  here**, which is a screen borrowing pieces through another screen rather than
  from where they live. Nothing imports them this way any more: each takes them
  from its own file, and My Counts is one shared screen rather than a component
  passed between two. Removed 5 September 2026.
*/
//  Desktop: History tab content 
//  Desktop: Tasks tab 
//  Desktop: Reports tab 
//  Main Component 
export default function RepDashboard() {
  // Which layout tree to mount. Same breakpoint the top bar uses, so the
  // mounted tree is always the one whose bar is on screen.
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const { profile }   = useAuth();
  const navigate      = useNavigate();
  const toast         = useToast();

  const [tab, setTabRaw]         = useState(() =>
    // **Home is where the app opens**, for a rep as for everybody.
    sessionStorage.getItem('repTab') || 'home');
  const [cycle, setCycle]        = useState(null);
  const [counts, setCounts]      = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading]    = useState(true);
  const [pendingRequests, setPendingRequests] = useState({});
  const [editRequestModal, setEditRequestModal] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [viewOnlyCount, setViewOnlyCount]       = useState(null);

  // Mobile-specific state
  // **`mobileTab` is gone, 5 September 2026.** It was a second memory of which
  // page a rep is on, with its own names, and only the other one was written
  // down. Changing device lost your place, and the two disagreed: "Locations"
  // here was "My Counts" on a desk. One piece of state now, one set of keys,
  // from `src/lib/menu.jsx`.
  const [notifications, setNotifications]        = useState([]);

  function setTab(t) { sessionStorage.setItem('repTab', t); setTabRaw(t); }

  // **The menu, once, from `src/lib/menu.jsx`.** This screen had two of the six
  // and they disagreed with each other: the desk said My Counts, Tasks,
  // Reports, History and the dock below 1024 said Locations, History, Tasks &
  // Alerts, Sign Out. "My Counts" and "Locations" are the same screen and
  // "Reports" is called My Exports now, which Group C settled on 31 August 2026
  // because it collided with the four admin reports.
  const AREAS = menuFor('rep');


  useEffect(() => { loadData(); }, []);

  /*
    Refresh counts when the person comes back to the window, so lock states
    stay current.

    **Quietly** (the audit after the rebrand, issue 20, 18 September 2026). It
    used to put the whole page back to its loading outline, which threw away
    whatever the person was in the middle of: an open box, a half-typed note,
    the search and filter on My Counts. Clicking into another window and back
    was enough. The page now stays as it is and its numbers change under it.
  */
  useEffect(() => {
    const onFocus = () => { loadData({ quiet: true }); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []); // eslint-disable-line

  async function loadData({ quiet = false } = {}) {
    if (!quiet) setLoading(true);
    const { data: cycleData } = await supabase
      .from('count_cycles').select('*').eq('status', 'open').single();
    setCycle(cycleData || null);

    if (cycleData && profile?.id) {
      // My Counts = the locations I'm assigned to, joined to this cycle's sheets
      const myIds = await myWarehouseIds(profile.id);

      let countData = [];
      if (myIds.length > 0) {
        const { data } = await supabase
          .from('inventory_counts')
          // `display_name` and `region_prefix` are what a card prints: the
          // territory above, the clean name below. Without them a rep's cards
          // said "DFW-ASPEN GROVE ORTHOPEDICS" where a manager's said the name
          // (issue 29, 18 September 2026). `updated_at` is what "Carry on"
          // falls back on.
          .select('id, status, submitted_at, updated_at, rep_id, owner:profiles!inventory_counts_rep_id_fkey(full_name), warehouse:warehouses(id, name, display_name, region_prefix, allows_concurrent_counting, region:regions(name))')
          .eq('cycle_id', cycleData.id)
          .in('warehouse_id', myIds)
          .order('status');
        countData = data || [];
      }
      setCounts(countData);

      if (countData.length) {
        const { data: pendingTodos } = await supabase
          .from('todos').select('count_id')
          .in('count_id', countData.map(c => c.id))
          .eq('todo_type', 'edit_request')
          .eq('is_complete', false);
        const pending = {};
        for (const t of pendingTodos || []) pending[t.count_id] = true;
        setPendingRequests(pending);

        /*
          Every line on these sheets, with its cost, a page at a time.

          **It was one request** (the audit after the rebrand, issue 22, 18
          September 2026), which brings back about a thousand rows and says
          nothing about the rest. Michelle Chelette's 35 sheets hold 2,530
          lines, so her Home's "still blank" figure, her card bars and her
          money figures were counted off the first thousand, with nothing to
          say so. `last_updated_at` is what "Carry on" reads.
        */
        const { data: lineData } = await fetchAll(() => supabase
          .from('count_line_items')
          .select('id, inventory_count_id, quantity, last_updated_at, item:item_catalog_id(cost_per_item)')
          .in('inventory_count_id', countData.map(c => c.id))
          .order('id'));
        setLineItems(lineData || []);
      }
    }

    if (profile?.id) {
      const { data: notifData } = await supabase
        .from('alerts').select('*')
        .eq('rep_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(20);
      setNotifications(notifData || []);
    }

    setLoading(false);
  }

  /*
    **Marking notifications read moved to the Tasks screen**, which is the only
    place that shows them, so it is the only place that changes them. This file
    kept a second copy of the job and a second copy of the count beside it.
  */

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  function handleEditRequestSuccess(countId) {
    setPendingRequests(p => ({ ...p, [countId]: true }));
  }

  const stats = {
    not_started: counts.filter(c => c.status === 'not_started').length,
    in_progress:  counts.filter(c => c.status === 'in_progress').length,
    submitted:    counts.filter(c => c.status === 'submitted').length,
    approved:     counts.filter(c => c.status === 'approved').length,
  };
  const total      = counts.length;
  const pct        = total > 0 ? Math.round((stats.submitted + stats.approved) / total * 100) : 0;

  /*
    Home's numbers, counted off the same `counts` and `lineItems` this screen
    already holds.

    **`linesLeft` counts BLANK lines, not lines.** Rule 9: a blank means nobody
    has been to that shelf and a zero means somebody went and found none. The
    number a rep needs on opening the app is how many shelves are still
    unvisited, and counting zeros in would tell them the opposite of the truth.
  */
  // **`todos` is NOT in scope here.** It belongs to DesktopTasks, further up
  // this file, which loads the rep's tasks for itself. Home counts what the main
  // screen actually holds: the requests this rep has outstanding. Reaching for a
  // name that belongs to another component in the same file threw, and the page
  // came back BLANK with nothing in the picture to say why, which is exactly the
  // empty picture rule 22 warns about.
  const pendingCount = Object.values(pendingRequests || {}).filter(Boolean).length;

  const owed      = stats.not_started + stats.in_progress;
  const linesLeft = lineItems.filter(i => i.quantity === null || i.quantity === undefined).length;

  // The sheet to carry on with: see `pickCarryOn` in home/carryOn.js.
  const carryOn = useMemo(() => pickCarryOn(counts, lineItems), [counts, lineItems]);
  /*
    **The cycle's totals in items, units and dollars are gone**, 5 September
    2026. They headed a card on this screen and on My Counts, and Group C's
    drawing has neither: they were the whole count's totals shown on a screen
    about one person's locations, and the one figure a rep actually wants, how
    much is left, was not among them. Each card carries its own now.
  */
  // What each sheet holds, built the one way, so this screen and the loader in
  // cycle/MyCounts.jsx cannot disagree about what a card should say.
  const countValueMap = valueMapOf(lineItems);

  /*
    **A page loading keeps the shell**, rule 35 and Group H's approved drawing:
    the app is fine, the page is fine, it simply has not arrived. Until 10
    September 2026 this returned a spinner on a bare canvas, so for as long as
    the first read took, OnHand looked like it had nothing in it and offered no
    way anywhere.

    **It is `SystemShell` rather than a fourth copy of the bar**, so the menu
    here is the app's one menu and draws at both widths without this file
    knowing how. Same component the stopped screens use.
  */
  if (loading) return (
    <SystemShell>
      <PageLoading tall />
    </SystemShell>
  );

  //  DESKTOP LAYOUT
  /*
    **My Counts, built once and rendered at both widths**, 5 September 2026, to
    Group C's approved drawing.

    Until today this file held a whole second version of it below 1024, about 120
    lines with its own card shape, its own buttons and its own words. The two
    disagreed: a desk said "Submitted for Review" where a phone said "Submitted",
    and a phone's card showed a money figure where a desk's showed how many lines
    were counted.
  */
  const myCountsView = (
    <MyCountsView
      cycle={cycle}
      counts={counts}
      stats={stats}
      countValueMap={countValueMap}
      pendingRequests={pendingRequests}
      profile={profile}
      onOpen={c => navigate('/count/' + c.id)}
      onRequestEdit={c => setEditRequestModal(c)}
      onViewOnly={c => setViewOnlyCount(c)}
      onAddLocation={() => setShowRequestModal(true)}
    />
  );

  // The navigation the frames draw (rebrand step 4, src/components/AppFrame.jsx):
  // the same menu and the same page this screen already had.
  const nav = {
    areas: AREAS, activeKey: tab, onSelect: setTab,
    userName: profile?.full_name, email: profile?.email,
    roleLabel: 'Sales Rep', onSignOut: signOut,
  };

  const desktopLayout = (
    <DeskFrame nav={nav}    >
          {/* **"That did not save", with the button to try it again.** Group H's
              approved bar, built 10 September 2026. It draws nothing at all
              unless a save has just failed for want of a connection, which is
              almost always. Under the page bar and above the work, so nothing a
              person is reading moves out from under them. */}
          <SaveFailedBar />
          {tab === 'home' && (
            <RepHome
              profile={profile} cycle={cycle} stats={stats} total={total} pct={pct}
              owed={owed} linesLeft={linesLeft} carryOn={carryOn}
              taskCount={pendingCount}
              unreadCount={notifications.filter(n => !n.is_read).length}
              onContinue={id => navigate('/count/' + id)}
              onOpenTasks={() => setTab('tasks')}
              onOpenMyCounts={() => setTab('mycounts')}
            />
          )}

          {tab === 'mycounts' && myCountsView}
          {tab === 'tasks'   && <Tasks profile={profile} />}
          {tab === 'reports' && (
          <div style={{ padding: '0 14px' }}>
            <MyExports profile={profile} />
          </div>
        )}
          {tab === 'history' && <History profile={profile} navigate={navigate} />}
          {tab === 'appearance' && <Appearance />}
    </DeskFrame>
  );

  //  MOBILE LAYOUT 
  /* Floating glass header - identity only; Sign Out lives in the dock */
  /* Detached glass dock - floats above content, which scrolls under it */
  const mobileLayout = (
    <PhoneFrame nav={nav}
    >
        {/* The same bar below 1024. A component added to one of a screen's two
            layout trees and not the other is missing with nothing saying so,
            which is exactly what happened to the connection bar on this day. */}
        <SaveFailedBar />


        {/* Hero card */}
        {/* **Inside the same 16 pixels as the admin and manager Homes.** Fixed 13
            September 2026: it was placed with no wrapper at all, so on a phone the
            greeting, its sentence and every card ran to both edges of the screen.
            Nothing was clipped and nothing scrolled sideways, so no measurement
            could see it; a photograph of the rep's phone Home did, and the 11
            September photograph showed it had been that way before the redesign. */}
        {tab === 'home' && (
          <div>
            <RepHome
              profile={profile} cycle={cycle} stats={stats} total={total} pct={pct}
              owed={owed} linesLeft={linesLeft} carryOn={carryOn}
              taskCount={pendingCount}
              unreadCount={notifications.filter(n => !n.is_read).length}
              onContinue={id => navigate('/count/' + id)}
              onOpenTasks={() => setTab('tasks')}
              onOpenMyCounts={() => setTab('mycounts')}
            />
          </div>
        )}

        {tab === 'mycounts' && (
          <div>{myCountsView}</div>
        )}

        {/* History tab */}
        {/* My Exports had no route below 1024. Tasks and History already had
            their own held versions, at 'alerts' and 'history', so only this one
            was missing. */}
        {tab === 'reports' && (
          <div>
            <MyExports profile={profile} />
          </div>
        )}
        {tab === 'history' && (
          <div>
            <History profile={profile} navigate={navigate} />
          </div>
        )}
        {/* Appearance, in both trees (theme phase 5): a page added to one of this
            screen's two layouts and not the other is missing with nothing
            saying so. */}
        {tab === 'appearance' && (
          <div><Appearance /></div>
        )}

        {/* Alerts tab */}
        {/* **The held version of this screen left the whole "Raised by you"
            list out**, and was headed Alerts. A rep on a phone could not see
            one thing they had asked for: not an edit request, not a closure
            they had flagged, not a location they had picked up, with nothing
            on the screen to say a section was missing. One screen now. */}
        {tab === 'tasks' && (
          <div><Tasks profile={profile} /></div>
        )}


    </PhoneFrame>
  );

  const modal = editRequestModal && (
    <EditRequestModal
      count={editRequestModal}
      profile={profile}
      toast={toast}
      onClose={() => setEditRequestModal(null)}
      onSuccess={handleEditRequestSuccess}
    />
  );

  return (
    <>
      {/* Only the tree for the current layout is mounted. Hiding one with CSS
          alone left it rendering: its pages still portalled their controls into
          the visible top bar, so any page reachable from both trees put its
          control band in there twice (Settings, and the manager's My Counts).
          Same query PageBar tests, imported rather than retyped. */}
      <div className="rep-desktop-only">
        {isDesktop && desktopLayout}
      </div>
      <div className="rep-mobile-only">
        {!isDesktop && mobileLayout}
      </div>
      {modal}
      {showRequestModal && (
        <AddLocationModal
          profile={profile}
          cycle={cycle}
          toast={toast}
          onClose={() => setShowRequestModal(false)}
          onSuccess={loadData}
        />
      )}
      {viewOnlyCount && (
        <CountViewOnlyModal
          count={viewOnlyCount}
          profile={profile}
          toast={toast}
          onClose={() => setViewOnlyCount(null)}
        />
      )}
    </>
  );
}
